import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import HistoricalData from "~/pages/Editor/EditorCatalogEducationalPrograms/HistoricalData";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  CurriculumManagementProps,
  EntityData,
  IGroup,
  ILevel,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import type { PermissionKey } from "~/types/Permissions";

import s from "~/pages/Editor/EditorCatalogEducationalPrograms/EntityPages.less";

type CurriculumItem = IGroup | ILevel;

interface EntityPageProps {
  titleKey: string;
  createPermission: PermissionKey;
  updatePermission: PermissionKey;
  tableHeader: string[];
  getColumns: (t: TFunction, options: string[]) => ColumnsType<CurriculumItem>;
  useData: (page: number, count: number) => EntityData<CurriculumItem>;
  Management: React.ComponentType<CurriculumManagementProps>;
  formId: string;
  editTitleKey: string;
  addTitleKey: string;
}

const EntityPage = ({
  titleKey,
  createPermission,
  updatePermission,
  tableHeader,
  getColumns,
  useData,
  Management,
  formId,
  editTitleKey,
  addTitleKey,
}: EntityPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();
  const { count, page, openModal } = useGetLocationData();

  const { refetch, loading, tableItems, totalRecords } = useData(page, count);

  const { handleCount, handlePage } = usePaginationParams();

  const closeModal = () => setDataReplace("openModal", "", "currentId", null);

  const openAddModal = () => setDataReplace("openModal", OpenModal.Add);

  const handleRowEdit = (record: CurriculumItem) => {
    setDataReplace("openModal", OpenModal.Edit, "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t(titleKey)}
      txtBtn={t("ui.back")}
      goBackHandler={() => history.replace("/editor/cep/")}
      filterComponent={<HistoricalData refetch={refetch} />}
      controlComponent={
        hasPermission(createPermission) ? (
          <Button
            onClick={openAddModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("ui.add")}
          </Button>
        ) : undefined
      }
    >
      <TableV2<CurriculumItem>
        columns={getColumns(t, tableHeader)}
        dataSource={tableItems}
        loading={loading}
        {...(hasPermission(updatePermission)
          ? { onRowEdit: handleRowEdit }
          : {})}
        wrapperClassName={s.editor_table}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={totalRecords}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        open={openModal === OpenModal.Edit || openModal === OpenModal.Add}
        title={t(openModal === OpenModal.Edit ? editTitleKey : addTitleKey)}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("cep.save_btn"),
        }}
      >
        <Management formId={formId} refetch={refetch} closeModal={closeModal} />
      </Modal>
    </Wrapper.Content>
  );
};

export default EntityPage;
